import { AmpNavigation } from '@amplify/amp-navigation';
import React, { useEffect, useMemo, useState } from 'react';

import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useNavigationNotifications } from 'src/hooks/use-navigation-notifications.hook';
import { useUserAssets } from 'src/hooks/use-user-assets.hook';
import { XApiEventKeys } from 'src/interfaces/event.types';
import { getAmpConfigFlags } from 'src/services/amp-config.service';
import authService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import { setIsSingleSessionCodeModalOpen } from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';

import './student-nav.scss';

export interface Props {
  adultsView?: boolean;
}

// @TODO remove this after 2025 RFS release
const StudentNav = (props: Props) => {
  const { isNewNavigationEnabled } = getAmpConfigFlags();
  const dispatch = useAppDispatch();
  const [tokenUpdated, setTokenUpdated] = useState(false);

  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const adultsView = props.adultsView ?? false;

  const isStudent = authService.isStudent();

  const navigationNotifications = useNavigationNotifications();
  const notificationsToDisplay = isStudent
    ? navigationNotifications
    : undefined;

  useEffect(() => {
    const handleTokenRefresh = () => {
      // Toggle the state variable to trigger a re-render
      setTokenUpdated((prev) => !prev);
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
    };
  }, []);

  // isLoading is checked instead of isLoadingWithoutError to avoid hiding nav when there is assets query error
  const { data: userAssetsResponse, isLoading: isUserAssetsLoading } =
    useUserAssets();

  const handleSingleSessionCodeBtnClick = () => {
    dispatch(setIsSingleSessionCodeModalOpen(true));

    eventService.send(XApiEventKeys.classCodeModalOpened, undefined, {
      result: {
        isRightClick: false,
        ...commonXapiContextPayload,
      },
    });
  };

  const navConfig = useMemo(() => {
    const userAssets = userAssetsResponse ?? [];

    return {
      _keycloak: authService.getKC(),
      appName: 'student-home',
      adultsView,
      assets: userAssets,
      onSingleSessionCodeBtnClick: handleSingleSessionCodeBtnClick,
      notificationsToDisplay,
    };
    // eslint-disable-next-line
  }, [
    adultsView,
    userAssetsResponse,
    handleSingleSessionCodeBtnClick,
    tokenUpdated,
  ]);

  if (isUserAssetsLoading || isNewNavigationEnabled) return null;

  return (
    <section className="student-nav" data-testid="student-nav">
      <AmpNavigation config={navConfig} />
    </section>
  );
};

export default StudentNav;
